'use strict';

/**
 *
 * Request class allow to post easily the request to the api
 *
 * @example var request = new Request();
 *
 * @module core/request
 * @author Florian Kroockmann <florian.kroockmann@lp-digital.fr>
 */
module.exports = class Request {
  /**
   * Constructor initialize the data of the request
   */
  constructor() {
    this.url = '';
    this.method = 'GET';
    this.data = null;
    this.headers = {
      'Content-Type': 'application/x-www-form-uriencoded',
    };
  }

  /**
   * Set url of request
   *
   * @function setUrl
   * @memberof module:core/request
   * @example request.setUrl('localhost');
   * @param {string} url
   * @returns {Request} this
   */
  setUrl(url) {
    this.url = url;

    return this;
  }

  /**
   * Set the method of request and force uppercase
   *
   * @function setMethod
   * @memberof module:core/request
   * @example request.setMethod('POST');
   * @param {string} method
   * @returns {Request} this
   */
  setMethod(method) {
    this.method = method.toUpperCase();

    return this;
  }

  /**
   * Set the data of the request
   *
   * @function setData
   * @memberof module:core/request
   * @example request.setData({
   *    country: 'France,
   * });
   * @param {Mixed} data
   * @returns {Request} this
   */
  setData(data) {
    this.data = data;

    return this;
  }

  /**
   * Set all headers in request
   *
   * @function setHeaders
   * @memberof module:core/request
   * @example request.setHeaders({
   *    'Content-Type': 'application/x-www-form-uriencoded',
   * })
   * @param {object} headers
   * @returns {Request} this
   */
  setHeaders(headers) {
    this.headers = headers;

    return this;
  }

  /**
   * Set one header with name and value
   *
   * @function addHeader
   * @memberof module:core/request
   * @example request.addHeader('Accept', 'application/json');
   * @param {string} name
   * @param {string} value
   * @returns {Request} this
   */
  addHeader(name, value) {
    this.headers[name] = value;

    return this;
  }

  /**
   * Set content type of request
   * Shortcut of addHeader('Content-Type', '')
   *
   * @function setContentType
   * @memberof module:core/request
   * @example request.setContentType('application/json');
   * @param {string} contentType
   * @returns {Request} this
   */
  setContentType(contentType) {
    this.addHeader('Content-Type', contentType);

    return this;
  }

  /**
   * Get the url with query params
   *
   * @function getUrl
   * @memberof module:core/request
   * @example request.getUrl()
   * @returns {string} url builded
   */
  getUrl() {
    return this.url;
  }

  /**
   * Get the content type of request
   *
   * @function getContentType
   * @memberof module:core/request
   * @example request.getContentType()
   * @returns {string} content type
   */
  getContentType() {
    return this.headers['Content-Type'];
  }

  /**
   * Get the method of request
   *
   * @function getMethod
   * @memberof module:core/request
   * @example request.getMethod()
   * @returns {string} method
   */
  getMethod() {
    return this.method;
  }

  /**
   * Get the data of request
   *
   * @function getData
   * @memberof module:core/request
   * @example request.getData()
   * @returns {Mixed} data
   */
  getData() {
    return this.data;
  }

  /**
   * Get header by key
   *
   * @function getHeader
   * @memberof module:core/request
   * @param {string} key
   * @example request.getHeader('Content-Type')
   * @returns {object | null} header
   */
  getHeader(key) {
    return this.headers[key] || null;
  }

  /**
   * Get the headers of request
   *
   * @function getHeaders
   * @memberof module:core/request
   * @example request.getHeaders()
   * @returns {object} headers
   */
  getHeaders() {
    return this.headers;
  }
};
